import { Trans } from "@lingui/react/macro";
import {
  ANY_TIME_OF_DAY,
  rescheduleActivityOccurrence,
  changeActivityOccurrenceTimespan,
  type IRescheduledActivityOccurrence,
} from "@/api/Activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import type { ITimeOfDay } from "@medoma/models/activities";
import {
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { useLingui } from "@lingui/react/macro";
import {
  TimeSensitivityPicker,
  type ITimeSensitivity,
} from "@/forms/AddActivity/Scheduling/TimeSensitivityPicker";
import { getTimespan } from "@/forms/AddActivity/activityTimeUtils";
import { timespanToHours } from "@/forms/AddActivity/Scheduling/timeSlotsUtils";

type IEditTimeFields = {
  date: string;
  timeCategory: string;
  time: string;
  timeSensitivity?: ITimeSensitivity;
};

export const EditActivityOccurrenceStartTime = ({
  currentDateTime,
  currentTimeOfDay,
  currentTimespan,
  onSuccess,
  activityId,
  occurrenceId,
}: {
  currentDateTime: Date;
  currentTimeOfDay: ITimeOfDay;
  currentTimespan: ITimeSensitivity;
  onSuccess: () => void;
  activityId: string;
  occurrenceId: string;
}) => {
  const { t } = useLingui();
  const methods = useForm<IEditTimeFields>({
    defaultValues: {
      date: machineFormat(currentDateTime, "yyyy-MM-dd"),
      timeCategory: currentTimeOfDay,
      // The scenario we are interested in is when we change from Any to Specific, so we just look at a snapshot and don't watch for changes
      time:
        currentTimeOfDay === timeOfDaySchema.enum.Any
          ? ""
          : machineFormat(currentDateTime, "HH:mm"),
      timeSensitivity:
        currentTimeOfDay === timeOfDaySchema.enum.Any
          ? ""
          : timespanToHours(currentTimespan),
    },
  });

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
    watch,
  } = methods;

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (
      data: IRescheduledActivityOccurrence | { timespan: string },
    ) => {
      if ("date" in data && "time" in data) {
        // Full reschedule with date/time change
        return rescheduleActivityOccurrence(activityId, occurrenceId, data);
      } else {
        // Only span change
        return changeActivityOccurrenceTimespan(
          activityId,
          occurrenceId,
          data.timespan,
        );
      }
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const isAnyTimeOfDay = watch("timeCategory") === timeOfDaySchema.enum.Any;

  const timeCategoryOptions = {
    required: {
      value: true,
      message: t`Välj tid på dagen`,
    },
  };

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={handleSubmit((formData) => {
          const {
            date,
            time: formTime,
            timeCategory,
            timeSensitivity,
          } = formData;

          const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
          const time = isAnyTimeOfDay ? ANY_TIME_OF_DAY : formTime;

          const timespan =
            timeSensitivity === undefined
              ? undefined
              : getTimespan({
                  isAnyTimeOfDay,
                  timeSensitivity,
                });

          // Check if only the span changed
          const currentDate = machineFormat(currentDateTime, "yyyy-MM-dd");
          const currentTime =
            currentTimeOfDay === timeOfDaySchema.enum.Any
              ? ANY_TIME_OF_DAY
              : machineFormat(currentDateTime, "HH:mm");

          const dateChanged = date !== currentDate;
          const timeChanged = time !== currentTime;

          if (!dateChanged && !timeChanged && timespan !== undefined) {
            // Only span changed
            mutate({ timespan });
          } else {
            // Date or time changed, use full reschedule
            mutate({
              date: calendarDateSchema.decode(date),
              time,
              span: timespan,
            });
          }
        })}
      >
        {isPending || isSuccess ? (
          <Loading message={t`Ändrar tid`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <Form.Row type="flex">
              <InputField
                label={t`Datum`}
                showOptionalLabel={false}
                errorMessage={errors.date?.message}
                type="date"
                {...register(`date`, {
                  required: {
                    value: true,
                    message: t`Ange datum`,
                  },
                  min: {
                    value: machineFormat(new Date(), "yyyy-MM-dd"),
                    message: t`Datumet behöver vara idag eller senare`,
                  },
                })}
              />
            </Form.Row>
            <Form.Row>
              <RadioButtons
                legend={t`Tid på dagen`}
                errorMessage={errors.timeCategory?.message}
                orientation="horizontal"
              >
                <RadioButton
                  label={{ text: t(timeOfDayDictionary.Any.short) }}
                  visualStyle="framed"
                  value={timeOfDaySchema.enum.Any}
                  {...register(`timeCategory`, timeCategoryOptions)}
                />
                <RadioButton
                  label={{ text: t(timeOfDayDictionary.Specific.short) }}
                  visualStyle="framed"
                  value={timeOfDaySchema.enum.Specific}
                  {...register(`timeCategory`, timeCategoryOptions)}
                />
              </RadioButtons>
            </Form.Row>
            {isAnyTimeOfDay ? (
              <></>
            ) : (
              <>
                <Form.Row>
                  <InputField
                    label={t`Starttid`}
                    showOptionalLabel={false}
                    errorMessage={errors.time?.message}
                    type="time"
                    {...register(`time`, {
                      required: {
                        value: true,
                        message: t`Ange starttid`,
                      },
                      pattern: {
                        value: /^\d{1,2}:\d{2}$/,
                        message: t`Starttid anges på formatet HH:mm`,
                      },
                    })}
                  />
                </Form.Row>
                <Form.Row type="flex">
                  <TimeSensitivityPicker />
                </Form.Row>
              </>
            )}
            {isDirty ? (
              <FilledButton type="submit">
                <Trans>Spara ändringar</Trans>
              </FilledButton>
            ) : (
              <></>
            )}
          </>
        )}
      </Form>
    </FormProvider>
  );
};
